const SURVEYS_BASE_PATH = `${process.env.PUBLIC_URL || ""}/JSON/Surveys`;
const SURVEYS_DEFAULT_LANGUAGE = "EN";

const SURVEYS_LANGUAGE_MAP = {
  en: "EN",
  bg: "BG",
};

const SURVEYS_SUPPORTED_QUESTION_TYPES = new Set([
  "single_choice",
  "multiple_choice",
  "short_text",
  "long_text",
]);

const SURVEYS_CHOICE_QUESTION_TYPES = new Set([
  "single_choice",
  "multiple_choice",
]);

function normalizeSurveyLanguageCode(language) {
  const primaryCode = String(language || "")
    .trim()
    .toLowerCase()
    .split("-")[0];

  if (!primaryCode) {
    return SURVEYS_DEFAULT_LANGUAGE;
  }

  return SURVEYS_LANGUAGE_MAP[primaryCode] || SURVEYS_DEFAULT_LANGUAGE;
}

function normalizeSurveyContentKey(contentKey) {
  const normalized = String(contentKey || "").trim();
  if (!normalized || !/^[A-Za-z0-9_-]+$/.test(normalized)) {
    return "";
  }

  return normalized;
}

function buildSurveyJsonUrl(contentKey, languageCode) {
  const normalizedContentKey = normalizeSurveyContentKey(contentKey);
  if (!normalizedContentKey) {
    return "";
  }

  return `${SURVEYS_BASE_PATH}/${normalizedContentKey}/${languageCode}.json`;
}

function getSurveyJsonCandidates(contentKey, language) {
  const preferredLanguage = normalizeSurveyLanguageCode(language);
  const candidates = [
    buildSurveyJsonUrl(contentKey, preferredLanguage),
    buildSurveyJsonUrl(contentKey, SURVEYS_DEFAULT_LANGUAGE),
  ].filter(Boolean);

  return [...new Set(candidates)];
}

function createSurveyError(message, details = {}) {
  return {
    ok: false,
    error: message,
    ...details,
  };
}

function sanitizeSurveyOption(option, questionId) {
  if (!option || typeof option !== "object") {
    return null;
  }

  const value = String(option.value || "").trim();
  const label = String(option.label || "").trim();

  if (!value || !label) {
    return null;
  }

  return {
    value,
    label,
    questionId,
  };
}

function sanitizeSurveyQuestion(question, index) {
  if (!question || typeof question !== "object") {
    return {
      ok: false,
      error: `Question ${index + 1} is missing or invalid`,
    };
  }

  const id = String(question.id || "").trim();
  const type = String(question.type || "").trim();
  const label = String(question.label || "").trim();
  const required = Boolean(question.required);
  const helperText = String(question.helperText || "").trim();
  const validationMessage = String(question.validationMessage || "").trim();

  if (!id || !label || !SURVEYS_SUPPORTED_QUESTION_TYPES.has(type)) {
    return {
      ok: false,
      error: `Question ${index + 1} is missing required fields`,
    };
  }

  let options = [];
  let maxSelections = null;
  if (SURVEYS_CHOICE_QUESTION_TYPES.has(type)) {
    const rawOptions = Array.isArray(question.options) ? question.options : [];
    options = rawOptions
      .map((option) => sanitizeSurveyOption(option, id))
      .filter(Boolean);

    if (!options.length) {
      return {
        ok: false,
        error: `Choice question "${id}" must define at least one option`,
      };
    }
  }

  if (type === "multiple_choice") {
    const rawMaxSelections = question.maxSelections;
    if (rawMaxSelections !== undefined && rawMaxSelections !== null && rawMaxSelections !== "") {
      const normalizedMaxSelections = Number(rawMaxSelections);
      if (
        !Number.isInteger(normalizedMaxSelections) ||
        normalizedMaxSelections < 1
      ) {
        return {
          ok: false,
          error: `Multiple choice question "${id}" has an invalid maxSelections value`,
        };
      }
      maxSelections = normalizedMaxSelections;
    }
  }

  return {
    ok: true,
    question: {
      id,
      type,
      label,
      required,
      helperText,
      validationMessage,
      maxSelections,
      options,
    },
  };
}

function validateSurveySchema(rawSurvey) {
  if (!rawSurvey || typeof rawSurvey !== "object") {
    return createSurveyError("Survey content is missing or invalid");
  }

  const surveyId = String(rawSurvey.surveyId || "").trim();
  const title = String(rawSurvey.title || "").trim();
  const description = String(rawSurvey.description || "").trim();
  const submitLabel = String(rawSurvey.submitLabel || "").trim();
  const successMessage = String(rawSurvey.successMessage || "").trim();
  const rawQuestions = Array.isArray(rawSurvey.questions)
    ? rawSurvey.questions
    : [];

  if (!surveyId || !title || !rawQuestions.length) {
    return createSurveyError(
      "Survey content must include surveyId, title, and at least one question"
    );
  }

  const questions = [];
  for (let index = 0; index < rawQuestions.length; index += 1) {
    const result = sanitizeSurveyQuestion(rawQuestions[index], index);
    if (!result.ok) {
      return createSurveyError(result.error);
    }

    questions.push(result.question);
  }

  return {
    ok: true,
    survey: {
      surveyId,
      title,
      description,
      submitLabel,
      successMessage,
      questions,
    },
  };
}

async function fetchSurveyJson(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Failed loading survey JSON: ${response.status}`);
  }

  return response.json();
}

async function loadSurveyContent(contentKey, language, options) {
  const normalizedContentKey = normalizeSurveyContentKey(contentKey);
  if (!normalizedContentKey) {
    return createSurveyError("Survey content key is missing or invalid", {
      contentKey: "",
      languageCode: normalizeSurveyLanguageCode(language),
    });
  }

  const candidates = getSurveyJsonCandidates(normalizedContentKey, language);
  let lastError = null;

  for (const url of candidates) {
    try {
      const json = await fetchSurveyJson(url, options);
      const validation = validateSurveySchema(json);
      if (!validation.ok) {
        lastError = validation.error;
        continue;
      }

      return {
        ok: true,
        survey: validation.survey,
        contentKey: normalizedContentKey,
        languageCode: normalizeSurveyLanguageCode(language),
        resolvedUrl: url,
      };
    } catch (error) {
      lastError = error;
    }
  }

  return createSurveyError(
    lastError?.message || "Failed loading survey content",
    {
      contentKey: normalizedContentKey,
      languageCode: normalizeSurveyLanguageCode(language),
    }
  );
}

export {
  SURVEYS_CHOICE_QUESTION_TYPES,
  buildSurveyJsonUrl,
  getSurveyJsonCandidates,
  loadSurveyContent,
  normalizeSurveyContentKey,
  normalizeSurveyLanguageCode,
  validateSurveySchema,
};
